// src/components/wallet/DividendTokenRewards.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Web3                              from 'web3';
import distributorAbi                    from '../../contracts/dividendToken_1_distributorAbi.js';
import dividendTokenAbi                  from '../../contracts/dividendToken_1_Abi.js';
import { formatCurrencyExtended }        from '../../utils.js';
import { Button }                        from 'primereact/button';
import { Skeleton }                      from 'primereact/skeleton';
import { Progress }                      from '@nextui-org/react';

/* ===========================================================================
   Generic “DividendToken” reflection panel – self-contained
   (works for any Reflect fork that exposes the same ABI surface)
   ========================================================================== */
const DividendTokenRewards = React.memo(
({ token, account, contracts, tokenSelections,
   viewTotalDistributed = false, enableClaiming = false }) =>
{
  /* ───────────── state ───────────── */
  const [contractData, setContractData]  = useState(null);    // all fetched data
  const [unclaimed,    setUnclaimed]     = useState(0);
  const [claimed,      setClaimed]       = useState(0);
  const [isClaiming,   setIsClaiming]    = useState(false);
  const [isLoading,    setIsLoading]     = useState(true);
  const [tokenCtr,     setTokenCtr]      = useState(null);    // main ERC20/reflect token
  const [distCtr,      setDistCtr]       = useState(null);    // its distributor

  /* ───────────── helpers ───────────── */
  const web3 = useMemo(() => new Web3(window.ethereum), []);

  const buildCtr = useCallback(
    (addr, abi) => new web3.eth.Contract(abi, addr),
    [web3]
  );

  /* ───────────── bootstrap contracts ───────────── */
  useEffect(() => {
    if (!token?.address) return;
    setTokenCtr(buildCtr(token.address, dividendTokenAbi));
  }, [token?.address, buildCtr]);

  useEffect(() => {
    if (!tokenCtr) return;
    (async () => {
      const trackerAddr = await tokenCtr.methods.dividendTracker().call();
      setDistCtr(buildCtr(trackerAddr, distributorAbi));
    })();
  }, [tokenCtr, buildCtr]);

  /* ───────────── loader ───────────── */
  const loadAll = useCallback(async () => {
    if (!tokenCtr || !distCtr || !account) return;
    setIsLoading(true);

    try {
      /* ----- payout token list ----- */
      let payout = await tokenCtr.methods.getPayoutToken().call();
      // some contracts return a single address, others an array
      if (!Array.isArray(payout)) payout = [payout];

      const payoutTokens = payout.map(addr => {
        const meta = tokenSelections.find(t => t.address.toLowerCase() === addr.toLowerCase());
        return [addr, meta];                               // [address, enrichedMeta|undefined]
      });

      /* ----- core stats in parallel ----- */
      const [
        divInfo,
        totalDistRaw,
        holdersRaw,
        totalRealRaw,
      ] = await Promise.all([
        tokenCtr.methods.getAccountDividendsInfo(account).call(),
        tokenCtr.methods.getTotalDividendsDistributed().call(),
        tokenCtr.methods.getNumberOfDividendTokenHolders().call(),
        distCtr.methods.withdrawnDividendOf(account).call()
      ]);

      /* ----- store ----- */
      setUnclaimed(Number(divInfo[3]));
      setClaimed(Number(totalRealRaw));
      setContractData({
        totalDistributed : Number(totalDistRaw),
        dividendHolders  : Number(holdersRaw),
        payoutTokens
      });
    } catch (e) { console.error('DividendToken load error', e); }

    setIsLoading(false);
  }, [tokenCtr, distCtr, account, tokenSelections]);

  /* initial + periodic refresh */
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const id = setInterval(loadAll, 60_000);
    return () => clearInterval(id);
  }, [loadAll]);

  /* ───────────── claim ───────────── */
  const handleClaim = useCallback(async () => {
    if (!tokenCtr || isClaiming) return;
    setIsClaiming(true);
    try {
      await tokenCtr.methods.claim().send({ from: account, gas: 1_750_000 });
      await loadAll();
    } catch (e) { console.error('DividendToken claim error', e); }
    setIsClaiming(false);
  }, [tokenCtr, account, isClaiming, loadAll]);

  /* ───────────── price helper ───────────── */
  const WBNB      = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c'.toLowerCase();
  const WBNBprice = tokenSelections.find(t => t.address.toLowerCase() === WBNB)?.price || 0;

  /* ══════════════ render ══════════════ */
  return (
    <div style={{
      alignItems:'center',
      borderRadius:10,
      backgroundColor:'rgba(50,100,255,0.05)',
      padding:5,
      fontSize:11
    }}>

      {/* payout list ------------------------------------------------------ */}
      <div>
        Rewards:&nbsp;
        <strong>
          {isLoading
            ? <Skeleton width="120px" height="1rem" />
            : contractData?.payoutTokens
                ?.map(p => p[1] ? p[1].label : p[0].slice(0,6)+'…')
                .join(', ')
          }
        </strong>
      </div>

      {/* total distributed ------------------------------------------------ */}
      {viewTotalDistributed && (
        <div>
          Total Distributed:&nbsp;
          <strong>
            {isLoading
              ? <Skeleton width="100px" height="1rem" />
              : formatCurrencyExtended(
                  (contractData?.totalDistributed || 0) / 1e18 * WBNBprice
                )}
          </strong>
        </div>
      )}

      {/* holder count ----------------------------------------------------- */}
      {contractData?.dividendHolders > 0 && (
        <div>
          Holder Count:&nbsp;<strong>{contractData.dividendHolders}</strong>
        </div>
      )}

      {/* earned / pending ------------------------------------------------- */}
      {(claimed === 0 && unclaimed === 0) &&
        <div><strong>No rewards pending</strong></div>}

      {claimed > 0 && (
        <div>Earned:&nbsp;
          <strong>
            {formatCurrencyExtended((claimed / 1e18) * WBNBprice)}
          </strong>
        </div>
      )}

      {unclaimed > 0 && (
        <div>Pending:&nbsp;
          <strong>
            {formatCurrencyExtended((unclaimed / 1e18) * WBNBprice)}
          </strong>
        </div>
      )}

      {/* claim button ----------------------------------------------------- */}
      {unclaimed > 0 && enableClaiming && (
        isClaiming
          ? <Progress indeterminate style={{ marginTop:10 }} />
          : <Button
              rounded severity="warning" size="small"
              style={{ marginTop:10, width:200, justifyContent:'center' }}
              onClick={handleClaim} disabled={isClaiming}
            >
              Claim
            </Button>
      )}
    </div>
  );
});

export default DividendTokenRewards;
